import {
    computed,
    Directive,
    DoCheck,
    forwardRef,
    inject,
    Injector,
    input,
    OnDestroy,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';

/**
 * @class ReactiveLabelDirective
 * @description a small directive that utilizes `ControlValueAccessor` interface
 * to bind the usual angular form control state classes (like ng-valid, ng-touched
 * and so on) to the host, dependent on a `FormControl`s state. Use `formControlName`
 * to set which `FormControl` should be used.
 *
 * Also adds the class `-reactive` and, on the condition `FormControl.value` is neither
 * `undefined`, `null` or empty string, the class `-has-value`.
 * @example Use it together with an input to style the label dependent on the
 * inputs state. If you want an input label to turn green when it has valid value,
 * use it like this:
 * ```html
 * <label appReactiveLabel formControlName="age" for="input-age">Age:</label>
 * <input type="number" id="input-age" formControlName="age"/>
 * ```
 * and set styles for the specific state you want to target:
 * ```css
 * label.-reactive.-has-value.ng-valid {
 *     color: green;
 * }
 * ```
 * @usageNotes if you don't want the `-reactive` class on the host set reactive to `false`:
 * ```html
 * <label appReactiveLabel formControlName="age" [reactive]="false" for="input-age">Age:</label>
 * <input type="number" id="input-age" formControlName="age"/>
 * ```
 *
 */
@Directive({
    selector: '[appReactiveLabel]',
    standalone: true,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ReactiveLabelDirective),
            multi: true,
        },
    ],
    host: {
        '[class.-reactive]': 'reactive()',
        '[class.-has-value]': 'hasValue()',
        '[class.-is-disabled]': 'isDisabled()',
    },
})
export class ReactiveLabelDirective implements ControlValueAccessor, OnInit, DoCheck, OnDestroy {
    public reactive = input<boolean>(true);
    public forceDisable = input<boolean>(false);

    private control: FormControl | undefined;
    private controlValue: WritableSignal<string> = signal<string>(null);
    private controlDisabled: WritableSignal<boolean> = signal<boolean>(false);

    private subscriptions: Subscription[] = [];

    protected isDisabled = computed(() => this.forceDisable() || this.controlDisabled());
    protected hasValue = computed(() => {
        const nonValues = [undefined, null, ''];
        return !nonValues.includes(this.controlValue());
    });

    private injector = inject<Injector>(Injector);

    public ngOnInit(): void {
        this.bindToControl();
    }

    public ngDoCheck(): void {
        const newControl = this.injector.get(NgControl, null).control as FormControl;
        if (this.control !== newControl) {
            this.cleanUpSubscriptions();
            this.bindToControl();
        }
    }

    public ngOnDestroy(): void {
        this.cleanUpSubscriptions();
    }

    private bindToControl(): void {
        setTimeout(() => {
            const ngControl = this.injector.get(NgControl, null);
            if (ngControl) {
                this.control = ngControl.control as FormControl;
                this.subscriptions.push(
                    this.control.valueChanges.subscribe((value) => {
                        this.controlValue.set(value);
                    }),
                );

                this.subscriptions.push(
                    this.control.statusChanges.subscribe((status) => {
                        this.controlDisabled.set(status === 'DISABLED');
                    }),
                );

                this.controlValue.set(this.control.value);
                this.controlDisabled.set(this.control.disabled);
            } else {
                throw new Error('reactive label is missing form control binding');
            }
        });
    }

    private cleanUpSubscriptions(): void {
        this.subscriptions.forEach((subscribtion) => {
            subscribtion.unsubscribe();
        });
        this.subscriptions = [];
    }

    // we don't actually need those, but they are
    // mandatory when implementing ControlValueAccessor
    /* eslint-disable @typescript-eslint/no-explicit-any */
    /* eslint-disable @typescript-eslint/no-unused-vars */
    public writeValue(obj: any): void {}
    public registerOnChange(fn: any): void {}
    public registerOnTouched(fn: any): void {}
    /* eslint-enable */
}
